'use strict';
require('./partials/slide.js');
const { inViewEventName, outOfViewEventName } = require('./eventNames.js');
const { getDependencyClass } = require('../../lib/dependencies.js');

/**
 * <phoenix-flat-gallery> Web Component.
 *
 * @example
 *   <phoenix-flat-gallery config={
 *      items = [{ id: '1111', slug: 'test-1111' }, { id: '2222', slug: 'test-2222' }]
 *   }>
 *     <phoenix-flat-gallery-slide id="1111"></phoenix-flat-gallery-slide>
 *     <phoenix-flat-gallery-slide id="2222"></phoenix-flat-gallery-slide>
 *   </phoenix-flat-gallery>
 */
getDependencyClass('phoenix-page').then((pageClass) => {
    class PhoenixFlatGallery extends pageClass.PhoenixElement {
        get observedEmittedEvents() {
            return [inViewEventName, outOfViewEventName];
        }

        get initialHash() {
            if (!this._initialHash) {
                const _initialHash = window.location.hash.replace('#', '');
                let urlParams = new URLSearchParams(_initialHash);
                const hashGalleryId = urlParams.get('gid');
                const slideSlug = urlParams.get('pid');
                if (hashGalleryId === this.id) {
                    this._initialHash = this.items.find((slide) => slide.slug === slideSlug);
                }
            }
            return this._initialHash;
        }

        set slideId(id) {
            if (id) {
                this._slideId = id;
            }
        }

        get slideId() {
            return this._slideId;
        }

        constructor() {
            super();
            this.slides = {};
            this.items = this.config?.items || [];
            if (this.items.length) {
                this.items.forEach(({ id, slug }, index) => {
                    this.slides[id] = {
                        index,
                        inView: false,
                        slug,
                    };
                });

                this.removeAttribute('config');
            }

            // 1. If the url has gallery hash and the slide is in gallery
            // disable hash updates until the selected slide is in view
            if (this.initialHash && this.initialHash.id) {
                this._scrollToSlide(this.initialHash.id);
            }

            this.inViewDebounced = pageClass.util.debounce(this._slideInView.bind(this), 250);
        }

        connectedCallback() {
            super.connectedCallback();
        }

        emittedEventCallback(name, event = {}) {
            const slide = event && event.target;
            this.slideId = slide.id;
            if (!this.slideId || !this.slides[this.slideId]) {
                return;
            }

            switch (name) {
                case inViewEventName:
                    this.slideInView = this.slideId;
                    this.inViewDebounced();
                    break;
                case outOfViewEventName:
                    this._slideOutOfView();
                    break;
            }
        }

        _updateSlideInViewState(inView = true) {
            this.slides[this.slideId].inView = inView;

            if (inView) {
                this._updatePageHash();
            }
        }

        _slideInView() {
            this._updateSlideInViewState();
        }

        _slideOutOfView() {
            this._updateSlideInViewState(false);
        }

        _updatePageHash() {
            if (!this.slideInView) {
                return;
            }

            const id = this.slideInView;
            const slug = this.slides[id].slug;
            window.history.replaceState(
                undefined,
                undefined,
                `${window.location.pathname}${window.location.search}#gid=${this.id}&pid=${slug}`
            );
        }

        _scrollToSlide(id) {
            const selectedSlide = this.querySelector(`#${id}`);
            if (selectedSlide.scrollIntoView) {
                selectedSlide.scrollIntoView();
            } else {
                window.screenTop({
                    top: selectedSlide.offsetTop,
                });
            }
        }
    }

    window.customElements.define('phoenix-flat-gallery', PhoenixFlatGallery);
});
